import { useEffect, useRef } from 'react';
import styles from './SyllabusModal.module.css';
import CertsCarousel from '../certsCarousel/CertsCarousel';

import {
  LuShieldCheck,
  LuBookOpen,
  LuGraduationCap,
  LuPackage,
  LuCamera,
  LuHeartHandshake,
  LuBadgePercent,
  LuEye,
  LuFlaskConical,
  LuSyringe,
  LuSparkles,
  LuClipboardCheck,
  LuSprayCan,
  LuUsers,
  LuTrendingUp,
  LuBrain,
  LuScissors,
  LuPalette,
  LuPencilRuler,
  LuRuler,
} from 'react-icons/lu';

const getIcon = (text) => {
  if (text.includes('תעודה') || text.includes('הסמכה')) return <LuGraduationCap />;
  if (text.includes('חוברת') || text.includes('חומר עיוני')) return <LuBookOpen />;
  if (text.includes('ליווי')) return <LuHeartHandshake />;
  if (text.includes('שיווק') || text.includes('בידול')) return <LuTrendingUp />;
  if (text.includes('מיינדסט')) return <LuBrain />;
  if (text.includes('צילום') || text.includes('מאקרו')) return <LuCamera />;
  if (text.includes('ערכ')) return <LuPackage />;
  if (text.includes('קופון') || text.includes('הנחה')) return <LuBadgePercent />;
  if (text.includes('סטרליזציה') || text.includes('חיטוי') || text.includes('עיקור')) return <LuSprayCan />;
  if (text.includes('מורפולוגיה') || text.includes('מבנה')) return <LuEye />;
  if (text.includes('PH') || text.includes('חומרים') || text.includes('סיליקונים')) return <LuFlaskConical />;
  if (text.includes('מחלות') || text.includes('סכנות') || text.includes('בריאות')) return <LuSyringe />;
  if (text.includes('טכניקה') || text.includes('הרמה') || text.includes('תיקון')) return <LuSparkles />;
  if (text.includes('הצהרות')) return <LuClipboardCheck />;
  if (text.includes('מודליסטי') || text.includes('תרגול') || text.includes('צופה')) return <LuUsers />;
  if (text.includes('שרטוט') || text.includes('גזירה')) return <LuPencilRuler />;
  if (text.includes('מריטה') || text.includes('שעוו')) return <LuScissors />;
  if (text.includes('קולוריסטה') || text.includes('צבע')) return <LuPalette />;
  if (text.includes('מידה') || text.includes('התאם')) return <LuRuler />;
  if (text.includes('שלבי')) return <LuClipboardCheck />;
  return <LuShieldCheck />;
};

const SyllabusModal = ({ isOpen, course, onClose, onFormOpen }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !course) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`${styles.overlay} ${styles.overlayEnter}`}
      onClick={handleOverlayClick}
    >
      {/* Close button outside modal transform container - truly fixed */}
      <button className={styles.closeBtn} onClick={onClose} aria-label="סגירה">
        ✕
      </button>

      <div
        ref={scrollRef}
        className={`${styles.modal} ${styles.modalEnter}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Hero with Romi image ── */}
        <div className={styles.hero}>
          {course.bgImage && (
            <>
              <img src={course.bgImage} alt="" className={styles.heroImg} loading="lazy" />
              <div className={styles.heroOverlay} />
            </>
          )}
          <div className={styles.heroContent}>
            <h2 className={`${styles.courseTitle} ${styles.titleEnter}`}>
              {course.title}
            </h2>
          </div>
        </div>

        {/* ── Decorative divider ── */}
        <div className={`${styles.heroDivider} ${styles.dividerEnter}`}>
          <span className={styles.dividerLine} />
          <span className={styles.dividerDiamond}>✦</span>
          <span className={styles.dividerLine} />
        </div>

        {/* ── Scrollable content ── */}
        <div className={styles.body}>

          {/* INCLUDES */}
          {course.includes?.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>מה כולל הקורס</h3>
              <div className={styles.includesList}>
                {course.includes.map((item, i) => (
                  <div
                    key={i}
                    className={`${styles.includeItem} ${styles.itemSlideIn}`}
                    style={{ animationDelay: `${0.5 + i * 0.06}s` }}
                  >
                    <span className={styles.includeIcon}>{getIcon(item)}</span>
                    <span className={styles.includeText}>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* THEORY */}
          {course.theory?.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>חלק עיוני</h3>
              <div className={styles.topicGrid}>
                {course.theory.map((item, i) => (
                  <div
                    key={i}
                    className={`${styles.topicItem} ${styles.itemScaleIn}`}
                    style={{ animationDelay: `${0.8 + i * 0.06}s` }}
                  >
                    <span className={styles.topicIcon}>{getIcon(item)}</span>
                    <span className={styles.topicText}>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* PRACTICE */}
          {course.practice?.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>חלק מעשי</h3>
              <div className={styles.practiceList}>
                {course.practice.map((item, i) => (
                  <div
                    key={i}
                    className={`${styles.practiceItem} ${styles.itemFadeUp}`}
                    style={{ animationDelay: `${1.1 + i * 0.06}s` }}
                  >
                    <span className={styles.practiceNum}>{String(i + 1).padStart(2, '0')}</span>
                    <span className={styles.practiceText}>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* STUDENT CERTIFICATES - full width */}
        <div className={styles.certsSection}>
          <h3 className={styles.certsTitle}>הבנות שסיימו כבר עובדות</h3>
          <p className={styles.certsSubtitle}>את יכולה להיות הבאה בתור</p>
          <CertsCarousel small />
        </div>

        {/* ── Floating CTA - matches FirstScreen shelf ── */}
        <div className={styles.footer}>
          <div className={styles.ctaShelf}>
            <button className={styles.ctaButton} onClick={onFormOpen}>
              <span className={styles.ctaShine} />
              <span className={styles.ctaText}>
                אני רוצה לשמוע עוד על {course.title}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyllabusModal;
